import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ShieldAlert, IdCard, BadgeCheck, Clock, FileWarning,
  Mail, ArrowUpRight, RefreshCw, CheckCircle2, Send,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps a backend category -> display group, icon, label.
const CATEGORY_META = {
  unverified_npi: { group: 'npi', label: 'Unverified NPI', icon: IdCard },
  license_unverified: { group: 'license', label: 'License unverified', icon: BadgeCheck },
  license_expired: { group: 'license', label: 'License expired', icon: BadgeCheck },
  license_expiring: { group: 'license', label: 'License expiring', icon: Clock },
  sla_breach: { group: 'sla', label: 'SLA breach', icon: Clock },
  incomplete_profile: { group: 'profile', label: 'Thin profile', icon: FileWarning },
};

const FILTERS = [
  { value: 'all', label: 'All signals' },
  { value: 'npi', label: 'NPI' },
  { value: 'license', label: 'Licenses' },
  { value: 'sla', label: 'Referral SLA' },
  { value: 'profile', label: 'Profiles' },
];

const SEV_STYLE = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-cn-ink-700 dark:text-slate-300',
};
const SEV_DOT = { high: 'bg-red-500', medium: 'bg-amber-500', low: 'bg-slate-400' };

const StatTile = ({ testid, label, value, icon: Icon, tone }) => (
  <div
    data-testid={testid}
    className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white font-display mt-1">{value}</p>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

export const AdminTrustSafetyPanel = ({ onGoToReferrals }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [nudged, setNudged] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/trust-safety`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load Trust & Safety signals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const runAction = async (signal) => {
    setBusyId(signal.id);
    try {
      if (signal.action === 'verify_npi') {
        const { data: res } = await axios.post(
          `${API}/admin/clinicians/${signal.subject_id}/verify-npi`, {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.verified) toast.success(`NPI verified via NPPES — ${res.registry_name || signal.subject_name}`);
        else toast.warning(res.message || 'NPI could not be verified');
      } else if (signal.action === 'verify_license') {
        await axios.put(
          `${API}/admin/clinicians/${signal.subject_id}/verify`, {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(`${signal.subject_name} — license verified`);
      } else if (signal.action === 'review_referral') {
        if (onGoToReferrals) onGoToReferrals();
        setBusyId(null);
        return;
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const nudge = async (signal, reason) => {
    setBusyId(signal.id);
    try {
      await axios.post(
        `${API}/admin/trust-safety/nudge`,
        { clinician_id: signal.subject_id, reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Nudge sent to ${signal.subject_name}`);
      setNudged((m) => ({ ...m, [signal.id]: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send nudge');
    } finally {
      setBusyId(null);
    }
  };

  const summary = data?.summary || {};
  const allSignals = data?.signals || [];
  const signals = filter === 'all'
    ? allSignals
    : allSignals.filter((s) => CATEGORY_META[s.category]?.group === filter);

  return (
    <div data-testid="admin-trust-safety-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="trust-safety-summary">
        <StatTile testid="ts-stat-npi" label="Unverified NPIs" value={summary.unverified_npi ?? 0} icon={IdCard} tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300" />
        <StatTile testid="ts-stat-license" label="License issues" value={summary.license_issues ?? 0} icon={BadgeCheck} tone="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" />
        <StatTile testid="ts-stat-sla" label="Referral SLA breaches" value={summary.sla_breaches ?? 0} icon={Clock} tone="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300" />
        <StatTile testid="ts-stat-profile" label="Thin profiles (<60%)" value={summary.incomplete_profiles ?? 0} icon={FileWarning} tone="bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" />
      </div>

      {/* Filters + refresh */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`ts-filter-${f.value}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-cn-primary text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          data-testid="ts-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 hover:text-cn-primary transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : signals.length === 0 ? (
        <div className="py-12 text-center" data-testid="trust-safety-empty">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">All clear</h3>
          <p className="text-slate-500 dark:text-slate-400 mt-1">No at-risk signals in this view.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> {signals.length} signal(s) — most urgent first
          </p>
          {signals.map((s) => {
            const meta = CATEGORY_META[s.category] || { label: s.category, icon: ShieldAlert };
            const Icon = meta.icon;
            const busy = busyId === s.id;
            return (
              <div
                key={s.id}
                data-testid={`ts-signal-${s.id}`}
                className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 flex items-start justify-between gap-4 flex-wrap"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${SEV_DOT[s.severity] || 'bg-slate-400'}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className="w-4 h-4 text-slate-400" />
                      <p className="font-semibold text-slate-900 dark:text-white">{s.title}</p>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLE[s.severity] || ''}`}>{s.severity}</span>
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 dark:bg-cn-ink-700 text-slate-500 dark:text-slate-300">{meta.label}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                      <span className="font-medium text-slate-700 dark:text-slate-200">{s.subject_name}</span>
                      {s.subject_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {s.subject_email}</span>}
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">{s.detail}</p>
                  </div>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1.5">
                  {s.action === 'verify_npi' && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => runAction(s)} disabled={busy} data-testid={`ts-action-${s.id}`}
                        className="cn-btn cn-btn-primary !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <IdCard className="w-3.5 h-3.5" />} Run NPI check
                      </button>
                      {s.can_nudge && (
                        <button onClick={() => nudge(s, s.nudge_reason)} disabled={busy || nudged[s.id]} data-testid={`ts-nudge-${s.id}`}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-700 dark:text-slate-200 hover:text-cn-primary inline-flex items-center gap-1.5 disabled:opacity-50">
                          {nudged[s.id] ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Send className="w-3.5 h-3.5" />}
                          {nudged[s.id] ? 'Nudged' : 'Nudge'}
                        </button>
                      )}
                    </div>
                  )}
                  {s.action === 'verify_license' && (
                    <button onClick={() => runAction(s)} disabled={busy} data-testid={`ts-action-${s.id}`}
                      className="cn-btn cn-btn-primary !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />} Verify license
                    </button>
                  )}
                  {s.action === 'nudge' && (
                    <button onClick={() => nudge(s, s.nudge_reason)} disabled={busy || nudged[s.id]} data-testid={`ts-action-${s.id}`}
                      className="cn-btn cn-btn-primary !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : nudged[s.id] ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                      {nudged[s.id] ? 'Nudged' : (s.meta?.last_nudge_at ? 'Nudge again' : 'Nudge clinician')}
                    </button>
                  )}
                  {s.action === 'review_referral' && (
                    <button onClick={() => runAction(s)} data-testid={`ts-action-${s.id}`}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-700 dark:text-slate-200 hover:text-cn-primary inline-flex items-center gap-1.5">
                      Review <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(nudged[s.id] || (s.meta?.last_nudge_at && s.action !== 'nudge')) && (
                    <span className="text-[11px] text-slate-400" data-testid={`ts-nudged-hint-${s.id}`}>Reminder sent</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminTrustSafetyPanel;
